// app/frontend/components/ui/Forms/L2KPasswordInput.tsx
import { PasswordInput, PasswordInputProps } from "@mantine/core";
import "./forms.css";

export interface L2KPasswordInputProps extends PasswordInputProps {
  validationState?: "valid" | "invalid";
}

export function L2KPasswordInput({
  validationState,
  styles: stylesProp,
  className,
  ...props
}: L2KPasswordInputProps) {
  const baseStyles = () => {
    const inputStyles: Record<string, any> = {
      backgroundColor: "rgba(255, 255, 255, 0.03)",
      borderColor: "rgba(255, 152, 0, 0.2)",
      color: "#F0F0F0 !important",
      fontFamily: "'Public Sans', sans-serif",
      fontSize: "14px",
      "--input-color": "#F0F0F0",
      "--input-placeholder-color": "#B8B8B8",
      "&:focus-within": {
        borderColor: "#FF9800",
        boxShadow: "0 0 0 2px rgba(255, 152, 0, 0.15)",
      },
      "&:hover:not(:focus-within)": {
        borderColor: "rgba(255, 152, 0, 0.4)",
      },
      "&::placeholder": {
        color: "#B8B8B8 !important",
      },
      "&:-webkit-autofill": {
        WebkitBoxShadow: "0 0 0px 1000px rgba(13, 13, 13, 1) inset !important",
        WebkitTextFillColor: "#F0F0F0 !important",
        transition: "background-color 5000s ease-in-out 0s",
      },
      "&:-webkit-autofill:hover": {
        WebkitBoxShadow: "0 0 0px 1000px rgba(15, 15, 15, 1) inset !important",
      },
      "&:-webkit-autofill:focus": {
        WebkitBoxShadow: "0 0 0px 1000px rgba(13, 13, 13, 1) inset !important",
        borderColor: "#FF9800 !important",
      },
    };

    if (validationState === "valid") {
      inputStyles.borderColor = "rgba(34, 197, 94, 0.6)";
      inputStyles.boxShadow = "0 0 0 2px rgba(34, 197, 94, 0.15)";
    } else if (validationState === "invalid") {
      inputStyles.borderColor = "rgba(248, 113, 113, 0.6)";
      inputStyles.boxShadow = "0 0 0 2px rgba(248, 113, 113, 0.15)";
    }

    return {
      label: {
        color: "#F0F0F0",
        fontFamily: "'Public Sans', sans-serif",
        fontWeight: 500,
        marginBottom: 6,
        fontSize: 14,
      },
      input: inputStyles,
      innerInput: {
        color: "#F0F0F0",
        fontFamily: "'Public Sans', sans-serif",
        "&::placeholder": {
          color: "#B8B8B8",
        },
      },
      visibilityToggle: {
        color: "#B8B8B8",
        "&:hover": {
          color: "#FF9800",
          backgroundColor: "transparent",
        },
      },
      error: {
        color: "#ff6b6b",
        fontFamily: "'Public Sans', sans-serif",
      },
      description: {
        color: "#B8B8B8",
        fontFamily: "'Public Sans', sans-serif",
      },
    };
  };

  const mergeStyles = (
    base: ReturnType<typeof baseStyles>,
    override?: PasswordInputProps["styles"]
  ): PasswordInputProps["styles"] => {
    if (!override) {
      return base;
    }

    if (typeof override === "function") {
      return (theme, params, ctx) => {
        const evaluatedOverride = override(theme, params, ctx) as any;
        return {
          ...base,
          ...evaluatedOverride,
          input: {
            ...base.input,
            ...(evaluatedOverride?.input ?? {}),
          },
          label: {
            ...base.label,
            ...(evaluatedOverride?.label ?? {}),
          },
        };
      };
    }

    return {
      ...base,
      ...(override as any),
      input: {
        ...base.input,
        ...((override as any)?.input ?? {}),
      },
      label: {
        ...base.label,
        ...((override as any)?.label ?? {}),
      },
    };
  };

  const finalStyles = mergeStyles(baseStyles(), stylesProp);

  return (
    <PasswordInput
      radius="md"
      className={`l2k-password-input ${className || ""}`}
      styles={finalStyles}
      {...props}
    />
  );
}

export default L2KPasswordInput;
